import { DQLResult } from '@portal/dql-editor'
import dayjs from 'dayjs'
import getProperty from 'lodash/get'
import isEmpty from 'lodash/isEmpty'

import {
  booleanField,
  DATA_REQUEST_PATHS,
  dayJsValue,
  Device,
  DeviceDiskUsage,
  DeviceLogConfig,
  DeviceResponse,
  DeviceResponseType,
  DeviceStoreCollection,
  DeviceStoreMeta,
  DeviceSubscription,
  DeviceSubscriptions,
  DeviceSyncScopeEntry,
  DeviceSyncScopes,
  getResponseAttachmentId,
  getResponseStatus,
  isDeviceLogLevel,
  logAttachmentId,
  numberField,
  objectField,
  parseSdkVersion,
  stringField,
} from './'

const extractDeviceResponses = (
  result: DQLResult,
  responsesPath: string,
  requestedAtPath: string,
  responseType: DeviceResponseType,
  responseStatusPath: string,
): DeviceResponse[] => {
  const latestRequestTimestamp = stringField(result, requestedAtPath)
  const latestRequest = dayjs(latestRequestTimestamp)

  const responses = Object.keys(objectField(result, responsesPath))
    .map((timestamp) => ({
      timestamp: dayJsValue(timestamp),
      isLatest: dayjs(timestamp).isSame(latestRequest),
      attachmentId:
        getResponseAttachmentId(result, responsesPath, timestamp) || null,
      type: responseType,
    }))
    .filter((response): response is DeviceResponse => {
      return (
        response.timestamp !== undefined &&
        response.timestamp.isValid() &&
        typeof response.attachmentId === 'string'
      )
    })
    .sort((a, b) => (a.timestamp.isBefore(b.timestamp) ? 1 : -1))

  // Check if the latest request has an error status
  const latestResponse = responses.find((r) => r.isLatest)
  if (latestResponse && !latestResponse.attachmentId) {
    const status = getResponseStatus(result, responseStatusPath)
    if (status && !status.success) {
      latestResponse.errorMessage = status.message
    }
  }

  // If there's a latest request but no response yet, and it has an error, create a response entry
  if (latestRequestTimestamp && !latestResponse && latestRequest.isValid()) {
    const status = getResponseStatus(result, responseStatusPath)
    if (status && !status.success) {
      responses.unshift({
        timestamp: latestRequest,
        isLatest: true,
        attachmentId: null,
        type: responseType,
        errorMessage: status.message,
      })
    }
  }

  return responses
}

export const dqlResultToDevice = (result: DQLResult): Device => {
  const id = result._id
  const lastSeen = dayjs(stringField(result, 'last_updated_at'))

  // Extract latest request timestamps and attachment IDs
  // Logs
  const latestLogsRequestTimestamp = stringField(
    result,
    DATA_REQUEST_PATHS.logs.requestedAt,
  )
  const latestLogsRequest = dayjs(latestLogsRequestTimestamp)
  const latestLogsAttachmentId = latestLogsRequestTimestamp
    ? logAttachmentId(
        result,
        latestLogsRequestTimestamp,
        DATA_REQUEST_PATHS.logs.responses,
      )
    : undefined

  // Bundle
  const latestBundleRequestTimestamp = stringField(
    result,
    DATA_REQUEST_PATHS.bundle.requestedAt,
  )
  const latestBundleRequest = dayjs(latestBundleRequestTimestamp)
  const latestBundleAttachmentId = latestBundleRequestTimestamp
    ? getResponseAttachmentId(
        result,
        DATA_REQUEST_PATHS.bundle.responses,
        latestBundleRequestTimestamp,
      )
    : undefined

  // Flamegraph
  const latestFlamegraphRequestTimestamp = stringField(
    result,
    DATA_REQUEST_PATHS.flamegraph.requestedAt,
  )
  const latestFlamegraphRequest = dayjs(latestFlamegraphRequestTimestamp)
  const latestFlamegraphAttachmentId = latestFlamegraphRequestTimestamp
    ? getResponseAttachmentId(
        result,
        DATA_REQUEST_PATHS.flamegraph.responses,
        latestFlamegraphRequestTimestamp,
      )
    : undefined

  // Metrics
  const latestMetricsRequestTimestamp = stringField(
    result,
    DATA_REQUEST_PATHS.metrics.requestedAt,
  )
  const latestMetricsRequest = dayjs(latestMetricsRequestTimestamp)
  const latestMetricsAttachmentId = latestMetricsRequestTimestamp
    ? getResponseAttachmentId(
        result,
        DATA_REQUEST_PATHS.metrics.responses,
        latestMetricsRequestTimestamp,
      )
    : undefined

  // Extract all device responses and merge into a single array
  const deviceResponses = [
    ...extractDeviceResponses(
      result,
      DATA_REQUEST_PATHS.logs.responses,
      DATA_REQUEST_PATHS.logs.requestedAt,
      'logs',
      DATA_REQUEST_PATHS.logs.responseStatus,
    ),
    ...extractDeviceResponses(
      result,
      DATA_REQUEST_PATHS.bundle.responses,
      DATA_REQUEST_PATHS.bundle.requestedAt,
      'bundle',
      DATA_REQUEST_PATHS.bundle.responseStatus,
    ),
    ...extractDeviceResponses(
      result,
      DATA_REQUEST_PATHS.flamegraph.responses,
      DATA_REQUEST_PATHS.flamegraph.requestedAt,
      'flamegraph',
      DATA_REQUEST_PATHS.flamegraph.responseStatus,
    ),
    ...extractDeviceResponses(
      result,
      DATA_REQUEST_PATHS.metrics.responses,
      DATA_REQUEST_PATHS.metrics.requestedAt,
      'metrics',
      DATA_REQUEST_PATHS.metrics.responseStatus,
    ),
  ].sort((a, b) => (a.timestamp.isBefore(b.timestamp) ? 1 : -1))

  const mergedMetdata = {
    ...objectField(result, 'metadata'),
    ...objectField(result, 'peer_metadata'),
  }

  return {
    peerKey: typeof id === 'string' ? id : '',
    name: stringField(result, 'device_name'),
    platform: stringField(result, 'ditto_sdk_platform'),
    dittoSdkLanguage: stringField(result, 'ditto_sdk_language'),
    dittoSdkVersion: stringField(result, 'ditto_sdk_version'),
    dittoSdkVersionNumber: parseSdkVersion(
      stringField(result, 'ditto_sdk_version'),
    ),
    manufacturer: stringField(result, 'device_manufacturer'),
    model: stringField(result, 'device_model'),
    serial: stringField(result, 'device_serial'),
    lastSeen: lastSeen.isValid() ? lastSeen : undefined,
    latestLogsRequest: latestLogsRequest.isValid()
      ? latestLogsRequest
      : undefined,
    latestLogsAttachmentId: latestLogsAttachmentId || undefined,
    latestBundleRequest: latestBundleRequest.isValid()
      ? latestBundleRequest
      : undefined,
    latestBundleAttachmentId: latestBundleAttachmentId || undefined,
    latestFlamegraphRequest: latestFlamegraphRequest.isValid()
      ? latestFlamegraphRequest
      : undefined,
    latestFlamegraphAttachmentId: latestFlamegraphAttachmentId || undefined,
    latestMetricsRequest: latestMetricsRequest.isValid()
      ? latestMetricsRequest
      : undefined,
    latestMetricsAttachmentId: latestMetricsAttachmentId || undefined,
    deviceResponses,
    metadata: mergedMetdata,
    diskUsage: deviceDiskUsage(result) || undefined,
    subscriptions: deviceSubscriptions(result) || undefined,
    storeMeta: deviceStoreMeta(result) || undefined,
    syncScopes: deviceSyncScopes(result) || undefined,
    logConfig: deviceLogConfig(result) || undefined,
    rawDocument: result as Record<string, unknown>,
  }
}

export const deviceDiskUsage = (result: DQLResult): DeviceDiskUsage | null => {
  const usageRaw = objectField(result, 'device_disk_usage')

  // If the disk usage is not available, return null. If present, we will assume valid structure
  if (!usageRaw || isEmpty(usageRaw)) {
    return null
  }

  return {
    deviceTotal: numberField(result, 'device_disk_usage.device_total'),
    deviceAvailable: numberField(result, 'device_disk_usage.device_available'),
    dittoTotal: numberField(result, 'device_disk_usage.ditto_total'),
    dittoAttachments: numberField(
      result,
      'device_disk_usage.ditto_attachments',
    ),
    dittoReplication: numberField(
      result,
      'device_disk_usage.ditto_replication',
    ),
    dittoStore: numberField(result, 'device_disk_usage.ditto_store'),
  }
}

export const deviceSubscriptions = (
  result: DQLResult,
): DeviceSubscriptions | null => {
  const subscriptionsRaw = objectField(result, 'local_subscriptions')

  // If the subscriptions are not available, return null. If present, we will assume valid structure
  if (!subscriptionsRaw || !Array.isArray(subscriptionsRaw.queries)) {
    return null
  }

  const truncated = Boolean(subscriptionsRaw.truncated) || false
  const queries = subscriptionsRaw.queries
    .map((query) => {
      const queryRaw = getProperty(query, 'query')
      const docs = getProperty(query, 'docs')
      if (queryRaw) {
        return {
          query: queryRaw,
          docs: typeof docs === 'number' ? docs : undefined,
        }
      } else {
        return null
      }
    })
    .filter(Boolean) as DeviceSubscription[]

  return {
    truncated,
    queries,
  }
}

export const deviceStoreMeta = (result: DQLResult): DeviceStoreMeta | null => {
  const storeMetaRaw = objectField(result, 'store')

  // If the store meta is not available, return null. If present, we will assume valid structure
  if (!storeMetaRaw || isEmpty(storeMetaRaw)) {
    return null
  }

  const truncated = Boolean(storeMetaRaw.truncated) || false
  const userCollections = objectField(result, 'store.user_collections')
  const collections = Object.keys(userCollections).reduce(
    (acc, key) => ({
      ...acc,
      [key]: {
        numDocs: numberField(result, `store.user_collections.${key}.num_docs`),
        numTombstones: numberField(
          result,
          `store.user_collections.${key}.num_tombstones`,
        ),
      },
    }),
    {} as Record<string, DeviceStoreCollection>,
  )

  return {
    truncated,
    collections,
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const isSyncScopeSource = (
  value: unknown,
): value is DeviceSyncScopeEntry['setBy'] =>
  typeof value === 'string' &&
  ['startup', 'sdk', 'alter_system'].includes(value)

const parseSyncScopeEntries = (value: unknown): DeviceSyncScopeEntry[] =>
  (Array.isArray(value) ? value : [value]).flatMap((entry) => {
    if (!isRecord(entry)) {
      return []
    }

    const scope = entry.scope
    const setBy = entry.set_by

    if (typeof scope !== 'string' || !isSyncScopeSource(setBy)) {
      return []
    }

    return [{ scope, setBy }]
  })

export const deviceSyncScopes = (
  result: DQLResult,
): DeviceSyncScopes | null => {
  const syncScopesRaw = getProperty(result, 'sync_scopes')

  // Absent (undefined) or non-object — SDK doesn't report sync scopes,
  // or the value is malformed. Hide the section entirely.
  if (!isRecord(syncScopesRaw)) {
    return null
  }

  const collectionsRaw = objectField(result, 'sync_scopes.collections')

  // objectField's typeof check passes null and arrays, which would throw or
  // produce index keys below. Treat as malformed with no collections.
  if (!isRecord(collectionsRaw)) {
    return {
      truncated: Boolean(syncScopesRaw.truncated),
      total: typeof syncScopesRaw.total === 'number' ? syncScopesRaw.total : 0,
      collections: {},
    }
  }

  const legacyUserCollections = collectionsRaw.user_collections
  const userCollectionsRaw = isRecord(legacyUserCollections)
    ? legacyUserCollections
    : collectionsRaw

  const collections = Object.entries(userCollectionsRaw).reduce(
    (acc, [collection, entriesRaw]) => {
      if (collection.startsWith('__')) {
        return acc
      }

      const entries = parseSyncScopeEntries(entriesRaw)
      if (entries.length > 0) {
        acc[collection] = entries
      }
      return acc
    },
    {} as Record<string, DeviceSyncScopeEntry[]>,
  )

  return {
    truncated: Boolean(syncScopesRaw.truncated),
    total:
      typeof syncScopesRaw.total === 'number'
        ? syncScopesRaw.total
        : Object.keys(collections).length,
    collections,
  }
}

export const deviceLogConfig = (result: DQLResult): DeviceLogConfig | null => {
  const logConfigRaw = objectField(result, 'logs_config')

  // If the log config is not available, return null. If present, we will assume valid structure
  if (!logConfigRaw || isEmpty(logConfigRaw)) {
    return null
  }

  const enabled = booleanField(result, 'logs_config.enabled')
  const minimumLevel = stringField(result, 'logs_config.minimum_level')

  return {
    enabled,
    minimumLevel: isDeviceLogLevel(minimumLevel) ? minimumLevel : 'INFO',
  }
}
